import { useState, useEffect, useCallback } from "react";
import { SelectChangeEvent } from "@mui/material";
import { AreaTags } from "./components/AreaTags";
import { FieldTags } from "./components/FieldTags";
import { LineGraph } from "../../common/components/LineGraph";

import { Option } from "../../common/interface/interface";
import { useAreaTags } from "../../common/hooks/useAreaTags";
import { useFieldOptions } from "../../common/hooks/useFieldOptions";
import { useFieldGraphData } from "../../common/hooks/useFieldGraphData";
import { QuickTimeRange } from "./components/QuickTimeRange";
import { GraphVariateConfigRow } from "./config-row/GraphVariateConfigRow";
import { MultiVariateGraph } from "./components/MultivariateGraph";
import { RawDataTable } from "./components/RawDataTable";

export const DashboardV2 = () => {
  const { areaOptions } = useAreaTags();
  const [areaTag, setAreaTag] = useState<string>("");
  const { fieldOptions } = useFieldOptions(areaTag);
  const [selectedFields, setSelectedFields] = useState<Option[]>([]);

  const [selectedTimeRange, setSelectedTimeRange] = useState<
    string | { start: string; end: string }
  >("");

  const [chartViewMode, setChartViewMode] = useState<"individual" | "combined">(
    "individual"
  );

  const handleTimeRange = useCallback(
    (range: { start: string; end: string } | string) => {
      setSelectedTimeRange(range);
    },
    []
  );

  const isCustomRange = typeof selectedTimeRange === "object";

  const start = isCustomRange ? selectedTimeRange.start : selectedTimeRange;
  const end = isCustomRange ? selectedTimeRange.end : "now()";

  const { isloading, graphData } = useFieldGraphData(
    areaTag,
    selectedFields,
    start,
    end
  );

  useEffect(() => {
    if (!areaTag && areaOptions.length > 0) {
      setAreaTag(areaOptions[0].value);
    }
  }, [areaOptions, areaTag]);

  useEffect(() => {
    if (fieldOptions.length > 0) {
      const fieldsToSelect = fieldOptions.slice(0, 6);
      setSelectedFields(fieldsToSelect);
    } else {
      setSelectedFields([]);
    }
  }, [fieldOptions]);

  const handleFieldChange = (e: SelectChangeEvent<string[]>) => {
    const selectedValues = e.target.value as string[];
    const updatedField = fieldOptions.filter((opt) =>
      selectedValues.includes(opt.value)
    );
    setSelectedFields(updatedField);
  };

  return (
    <>
      <div>
        <AreaTags
          areaTags={areaTag}
          options={areaOptions}
          onChange={(e) => setAreaTag(e.target.value)}
        />

        <FieldTags
          fieldNames={selectedFields.map((f) => f.value)}
          options={fieldOptions}
          onChange={handleFieldChange}
        />

        <QuickTimeRange onTimeRangeChange={handleTimeRange} />

        <RawDataTable selectedFields={selectedFields} areaTag={areaTag} />
      </div>

      <GraphVariateConfigRow
        chartViewMode={chartViewMode}
        setChartViewMode={setChartViewMode}
      />

      <MultiVariateGraph
        chartViewMode={chartViewMode}
        graphData={graphData}
        graphDataLoading={isloading}
      />

      {selectedFields.map((field) => (
        <LineGraph
          key={field.value}
          fieldName={field.label}
          data={graphData[field.value] || []}
          loading={isloading}
        />
      ))}
    </>
  );
};
